<template>
	<div>
		<div class="account-type-grid">
			<AccountTypeLozenge v-for="accountType in accountTypes"
				:key="accountType.key"
				:accountType="accountType.label"
				:iconUrl="accountType.icon"
				:accountCount="getAccountCount(accountType.label)"
				:isAddButton="getAccountCount(accountType.label) === 0"
				:compactMode="cols !== 1"
				:text="accountType.text"
				:textIcon="accountType.textIcon"
				@click="handleAccountTypeClick(accountType)"
			/>
		</div>
		<MGButton
			v-if="showAddButton"
			class="add-account-button"
			:fullWidth="true"
			:noCaps="true"
			:text="`Add an account`"
			:plus="true"
			@click="handleAddAccountClick"
		/>
	</div>
</template>

<script>
import AccountTypeLozenge from './AccountTypeLozenge.vue';
import MGButton from './MGButton.vue';

export default {
	name: 'AccountTypeGrid',
	components: {
		AccountTypeLozenge, MGButton
	},
	emits: ['account-type-click', 'add-account-click', 'alt-action'],
	props: {
		currentAccounts: {
			type: Array,
			default: () => []
		},
		creditAccounts: {
			type: Array,
			default: () => []
		},
		savingAccounts: {
			type: Array,
			default: () => []
		},
		insuranceInfo: {
			type: Array,
			default: () => []
		},
		mortgageInfo: {
			type: Array,
			default: () => []
		},
		creditInfo: {
			type: Array,
			default: () => []
		},
		investmentInfo: {
			type: Array,
			default: () => []
		},
		suggestedAssets: {
			type: Array,
			default: () => []
		},
		cols: {
			type: Number,
			default: 2
		},
		showAddButton: {
			type: Boolean,
			default: true
		},
		filter: {
			type: String,
			default: null
		},
		offline: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			allAccountTypes: [
				{
					label: 'Current Account',
					text: 'Connect with <b>OPEN BANKING</b>',
					textIcon: require('@/assets/figureofeight.png'),
					altAction: 'openAddCurrentAccountDrawer',
					icon: require('@/assets/accountTypes/current-account.png'),
					walkthrough: 'money-account-types',
					key: 'current-account'
				},
				{
					label: 'Credit Card',
					text: 'Connect with <b>OPEN BANKING</b>',
					textIcon: require('@/assets/figureofeight.png'),
					altAction: 'openAddCurrentAccountDrawer',
					icon: require('@/assets/accountTypes/credit-card.png'),
					walkthrough: 'money-account-types',
					key: 'credit-card'
				},
				{
					label: 'Savings',
					text: 'Connect with <b>OPEN BANKING</b>',
					textIcon: require('@/assets/figureofeight.png'),
					altAction: 'openAddCurrentAccountDrawer',
					icon: require('@/assets/accountTypes/savings-account.png'),
					walkthrough: 'money-account-types',
					key: 'savings-account'
				},
				{
					label: 'Investments',
					icon: require('@/assets/accountTypes/investments.png'),
					walkthrough: 'money-account-types',
					key: 'investments'
				},
				{
					label: 'Pension',
					icon: require('@/assets/accountTypes/pension.png'),
					walkthrough: 'money-account-types',
					key: 'pension'
				},
				{
					label: 'Mortgage',
					icon: require('@/assets/accountTypes/mortgage.png'),
					walkthrough: 'money-account-types',
					key: 'mortgage'
				},
				{
					label: 'Loans',
					icon: require('@/assets/accountTypes/loans.png'),
					walkthrough: 'money-account-types',
					key: 'loans'
				},
				{
					label: 'Insurance Policy',
					icon: require('@/assets/accountTypes/insurance.png'),
					walkthrough: 'money-account-types',
					key: 'insurance-policy'
				}
			]
		}
	},
	computed: {
		accountTypes() {
			let types = this.allAccountTypes;

			const longTermKeys = ['investments', 'pension', 'mortgage', 'loans'];
			const dayToDayKeys = ['current-account', 'credit-card', 'savings-account'];

			if (this.filter === 'lt') {
				types = types.filter(t => longTermKeys.includes(t.key));
			} else if (this.filter === 'd2d') {
				types = types.filter(t => dayToDayKeys.includes(t.key));
			}

			if (this.offline) {
				return types
					.filter(t => t.key !== 'savings-account' && t.key !== 'credit-card')
					.map(t => ({
						...t,
						text: null,
						textIcon: null
					}));
			}

			return types;
		}
	},
	methods: {
		suggestedCount(sectionPrefix) {
			if (!this.suggestedAssets) return 0;
			return this.suggestedAssets.filter(a => {
				const s = a.uiElement?.section || '';
				return s === sectionPrefix || s.startsWith(sectionPrefix + ' >');
			}).length;
		},
		getAccountCount(accountTypeLabel) {
			switch(accountTypeLabel) {
				case 'Current Account':
					return this.currentAccounts.length + this.suggestedCount('Day-to-day > Current');
				case 'Credit Card':
					return this.creditAccounts.length + this.suggestedCount('Day-to-day > Credit');
				case 'Savings':
					return this.savingAccounts.length + this.suggestedCount('Day-to-day > Savings');
				case 'Insurance Policy':
					return this.insuranceInfo.length + this.suggestedCount('Insurance');
				case 'Mortgage':
					return this.mortgageInfo.length + this.suggestedCount('Long-term > Mortgages');
				case 'Loans':
					return this.creditInfo.length + this.suggestedCount('Long-term > Loans');
				case 'Pension':
					return this.investmentInfo.filter(account => account.accountType?.name === 'Pension').length + this.suggestedCount('Long-term > Pensions');
				case 'Investments':
					return this.investmentInfo.filter(account => account.accountType?.name === 'Investment').length + this.suggestedCount('Long-term > Investments');
				default:
					return 0;
			}
		},
		handleAddAccountClick() {
			this.$emit('add-account-click');
		},
		handleAccountTypeClick(accountType) {
			// If offline mode, navigate directly to add routes
			if (this.offline) {
				const routes = {
					'current-account': '/Accounts/Current/Add',
					'credit-card': '/Credit/Add',
					'savings-account': '/Accounts/Savings/Add',
					'investments': '/Investments/Add',
					'pension': '/Pensions/Add',
					'mortgage': '/Mortgages/Add',
					'loans': '/Credit/Add',
					'insurance-policy': '/Policies/Insurance/Add'
				};
				if (routes[accountType.key]) {
					this.$router.push(routes[accountType.key]);
				}
				return;
			}
			// If altAction exists and we're in compact mode, emit alt-action event
			if (accountType.altAction && this.cols === 1) {
				this.$emit('alt-action', accountType);
			} else {
				this.$emit('account-type-click', accountType);
			}
		}
	}
}
</script>

<style scoped>
.account-type-grid {
	display: grid;
	grid-template-columns: v-bind("'repeat(' + cols + ', 1fr)'");
	gap: 8px;
}
</style>
